import { z } from 'zod';
import { gtinPruefziffer, ProduktStatus, type ProduktStatus as Status, type ProduktZaehler, type Qualitaetsregeln } from './produkte';

/**
 * Händlerportal → Produkte (mp_my_products, mp_product_get/save, mp_manuell_*, mp_bild_hoch, mp_ai_text der PHP-Middleware).
 * Der Händler sieht und pflegt nur seine eigenen Produkte. Prüfung und Mängel wie im Backoffice.
 */

/** Grenzen wie lib_produktpflege.php */
export const PORTAL_PRODUKT_GRENZEN = {
  /** PP_MAX_VARIANTEN */
  varianten: 100,
  /** Optionen je Produkt (z. B. Größe, Farbe) */
  optionen: 3,
  bilder: 20,
  /** gespeichertes Bild: höchstens 1 MB (PP_BILD_ZIEL) … */
  bildBytes: 1_000_000,
  /** … und 2000 px an der längsten Seite (PP_BILD_KANTE) */
  bildKante: 2000,
  /** mindestens 200 × 200 px */
  bildMin: 200,
  /** angenommene Datei vor dem Verkleinern im Browser (PP_BILD_ROH_MAX) */
  bildRoh: 25_000_000,
} as const;

export const PORTAL_BILD_TYPEN = ['image/png', 'image/jpeg', 'image/webp'] as const;

export interface PortalProduktZeile {
  id: string;
  titel: string;
  marke: string;
  bild: string | null;
  bilder: number;
  kategorie: string;
  status: Status;
  statusGrund: string;
  qualitaetOk: boolean;
  maengel: string[];
  varianten: number;
  sku: string;
  ean: string;
  preisVon: number | null;
  preisBis: number | null;
  bestand: number | null;
  quelle: string;
  erstelltAm: string;
}

export interface PortalProduktSeite {
  eintraege: PortalProduktZeile[];
  weiter: string | null;
  gesamt: number;
  gesamtGenau: boolean;
  /** je Status über die eigenen Produkte */
  zaehler: ProduktZaehler;
}

const jaNein = z.preprocess((v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : v), z.boolean());

export const PortalProduktAbfrage = z.object({
  q: z.string().trim().max(100).optional(),
  status: ProduktStatus.optional(),
  maengel: jaNein.optional(),
  sortierung: z.enum(['neu', 'titel']).default('neu'),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Freigegebenes Produktfeld, wie der Händler es ausfüllt (mp_metafields) */
export interface PortalProduktFeld {
  /** namespace.schluessel */
  voll: string;
  name: string;
  beschreibung: string;
  typ: string;
  auswahl: string[];
  pflicht: boolean;
  /** Gruppe der Branchenvorlage, leer = Weitere Angaben */
  gruppe: string;
  /** Einheit bei Zahlen, z. B. „mm“ */
  einheit: string;
}

/** Was der Editor braucht: Kategorien, freigegebene Felder, Qualitätsregeln */
export interface PortalProduktFormular {
  kategorien: string[];
  felder: PortalProduktFeld[];
  /** Reihenfolge der Gruppen (Branchenvorlage) */
  gruppen: string[];
  regeln: Qualitaetsregeln;
}

export interface PortalProduktVariante {
  id: string;
  /** Wert je Option ({"Größe": "M"}) */
  werte: Record<string, string>;
  sku: string;
  ean: string;
  preis: number | null;
  preisAlt: number | null;
  bestand: number | null;
  /** im Marktplatz-Shop angelegt – lässt sich nicht entfernen */
  gelistet: boolean;
}

export interface PortalProduktDetail {
  id: string;
  titel: string;
  beschreibung: string;
  marke: string;
  kategorie: string;
  bilder: string[];
  felder: Record<string, string>;
  optionen: string[];
  varianten: PortalProduktVariante[];
  quelle: string;
  status: Status;
  statusGrund: string;
  qualitaetOk: boolean;
  maengel: string[];
  hinweise: string[];
  /** im Marktplatz-Shop gelistet – Löschen nicht möglich */
  gelistet: boolean;
  /** Sperrgut: Rücksendung per Abholung statt Label (Retouren) */
  sperrgut: boolean;
  /** Eigener Shopify-Shop des Händlers (über die App „DXL Marketplace“): verbunden? Produkt dort schon vorhanden? */
  haendlerShop: { shop: string | null; produktId: string | null };
  erstelltAm: string;
  geaendertAm: string;
}

const kurz = (max: number) => z.string().trim().max(max).default('');
const zahlEingabe = z.preprocess((v) => {
  if (v === '' || v === null || v === undefined) return null;
  if (typeof v === 'string') return Number(v.replace(/\s+/g, '').replace(',', '.'));
  return v;
}, z.number({ invalid_type_error: 'Bitte eine Zahl angeben' }).finite('Bitte eine Zahl angeben').nullable());

export const PortalVarianteSchema = z.object({
  id: z.string().uuid().optional(),
  werte: z.record(z.string().max(40), z.string().max(200)).default({}),
  sku: z.string().trim().max(80, 'Höchstens 80 Zeichen').default(''),
  ean: z.string().max(40).default('').transform((v) => v.replace(/\D/g, '')),
  preis: zahlEingabe,
  preisAlt: zahlEingabe.default(null),
  bestand: zahlEingabe.default(null),
});
export type PortalVarianteEingabe = z.input<typeof PortalVarianteSchema>;

/**
 * Produkt anlegen oder ändern (pp_speichern): Titel, mindestens eine Variante mit SKU und Preis,
 * EAN mit Prüfziffer, bis zu drei Optionen; mehrere Varianten brauchen eine Option.
 */
export const PortalProduktSchema = z
  .object({
    titel: z.string().transform((v) => v.replace(/\s+/g, ' ').trim()).pipe(z.string().min(1, 'Bitte einen Produkttitel angeben.').max(200, 'Höchstens 200 Zeichen')),
    beschreibung: z.string().max(100_000).default('').transform((v) => v.trim()),
    marke: kurz(120),
    kategorie: kurz(120),
    bilder: z.array(z.string().trim().max(2000)).max(PORTAL_PRODUKT_GRENZEN.bilder, `Höchstens ${PORTAL_PRODUKT_GRENZEN.bilder} Bilder`).default([])
      .transform((l) => [...new Set(l.filter((u) => /^https?:\/\//i.test(u)))]),
    felder: z.record(z.string().max(130), z.union([z.string().max(2000), z.number(), z.boolean()])).default({}),
    optionen: z.array(z.string().trim().max(40, 'Höchstens 40 Zeichen')).max(PORTAL_PRODUKT_GRENZEN.optionen, 'Höchstens drei Optionen').default([])
      .transform((l) => l.filter(Boolean)),
    varianten: z.array(PortalVarianteSchema).min(1, 'Bitte mindestens eine Variante mit SKU und Preis angeben.')
      .max(PORTAL_PRODUKT_GRENZEN.varianten, `Höchstens ${PORTAL_PRODUKT_GRENZEN.varianten} Varianten je Produkt.`),
  })
  .superRefine((d, ctx) => {
    const fehler = (path: (string | number)[], message: string) => ctx.addIssue({ code: 'custom', path, message });
    const namen = new Set<string>();
    d.optionen.forEach((o, i) => {
      if (namen.has(o.toLowerCase())) fehler(['optionen', i], 'Diese Option gibt es schon');
      namen.add(o.toLowerCase());
    });
    if (!d.optionen.length && d.varianten.length > 1) fehler(['optionen'], 'Mehrere Varianten brauchen mindestens eine Option (z. B. Größe).');
    const skus = new Set<string>();
    const kombis = new Set<string>();
    d.varianten.forEach((v, i) => {
      const nr = i + 1;
      if (!v.sku) fehler(['varianten', i, 'sku'], `Variante ${nr}: Bitte eine SKU (Artikelnummer) angeben.`);
      else if (skus.has(v.sku.toLowerCase())) fehler(['varianten', i, 'sku'], `Die SKU „${v.sku}“ kommt mehrfach vor.`);
      skus.add(v.sku.toLowerCase());
      if (v.ean && (![8, 12, 13, 14].includes(v.ean.length) || !gtinPruefziffer(v.ean))) fehler(['varianten', i, 'ean'], `Variante ${nr}: Die EAN „${v.ean}“ ist ungültig (Prüfziffer).`);
      if (v.preis === null || v.preis <= 0) fehler(['varianten', i, 'preis'], `Variante ${nr}: Bitte einen Preis angeben.`);
      else if (v.preis > 9_999_999) fehler(['varianten', i, 'preis'], 'Der Preis ist zu hoch.');
      if (v.preisAlt !== null && (v.preisAlt < 0 || v.preisAlt > 9_999_999)) fehler(['varianten', i, 'preisAlt'], 'Ungültiger Preis.');
      if (v.bestand !== null && (!Number.isInteger(v.bestand) || v.bestand < 0 || v.bestand > 10_000_000)) fehler(['varianten', i, 'bestand'], 'Bitte eine ganze Zahl ab 0 angeben.');
      const werte: string[] = [];
      for (const o of d.optionen) {
        const w = (v.werte[o] ?? '').trim();
        if (!w) fehler(['varianten', i, 'werte', o], `Variante ${nr}: Bitte einen Wert für „${o}“ angeben.`);
        else if (w.length > 80) fehler(['varianten', i, 'werte', o], 'Höchstens 80 Zeichen');
        werte.push(w.toLowerCase());
      }
      const kombi = werte.join('|');
      if (d.optionen.length && werte.every(Boolean)) {
        if (kombis.has(kombi)) fehler(['varianten', i, 'werte', d.optionen[0]!], `Die Variante „${d.optionen.map((o) => v.werte[o]?.trim()).join(' / ')}“ kommt doppelt vor.`);
        kombis.add(kombi);
      }
    });
  });
export type PortalProduktEingabe = z.input<typeof PortalProduktSchema>;
export type PortalProduktDaten = z.output<typeof PortalProduktSchema>;

export interface PortalBildErgebnis {
  url: string;
  breite: number;
  hoehe: number;
  bytes: number;
}

